// QRPaymentCapture Component
// Primary: UPI Payment (QR Code + UPI ID) | Secondary: Card/NetBanking | Fallback: Manual Payment

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Alert,
  Stack,
  Divider,
  CircularProgress,
  Chip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Input,
  FormControl,
  FormLabel,
  IconButton,
} from '@mui/joy';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { supabase } from '../utils/supabaseClient';
import { useIsMobile } from '../hooks/useIsMobile';

interface SubscriptionPlan {
  id: string;
  plan_name: 'silver' | 'gold';
  billing_cycle: 'monthly' | 'yearly';
  price_inr: number;
  price_usd: number;
  display_name: string;
  description: string;
  features_json: string[];
}

interface QRPaymentCaptureProps {
  plan: SubscriptionPlan;
  userEmail: string;
  userDetails: {
    full_name?: string;
    cell_number?: string;
    country_code?: string;
  };
  region: 'india' | 'international';
  onSuccess: (subscriptionId: string) => void;
  onBack: () => void;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

export default function QRPaymentCapture({
  plan,
  userEmail,
  userDetails,
  region,
  onSuccess,
  onBack,
}: QRPaymentCaptureProps) {
  const [activeTab, setActiveTab] = useState<number>(0); // 0: UPI (QR+ID), 1: Card, 2: Manual
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [upiId, setUpiId] = useState<string>('');
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [currency] = useState<'INR' | 'USD'>(region === 'india' ? 'INR' : 'USD');
  const [manualPaymentNotified, setManualPaymentNotified] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    // Load Razorpay script on mount
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingInterval]);

  const createOrder = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Please log in to continue');
    }

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-subscription-order`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          plan_id: plan.id,
          user_email: userEmail,
          user_details: userDetails,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create order');
    }

    return await response.json();
  };

  const generateQRCode = async () => {
    setProcessing(true);
    setError(null);

    try {
      const { order, subscription_id } = await createOrder();
      setOrderId(order.id);
      setSubscriptionId(subscription_id);

      // Generate QR code via Razorpay API
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Session expired. Please log in again.');
      
      const qrResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-qr-code`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            order_id: order.id,
            amount: order.amount,
          }),
        }
      );

      if (!qrResponse.ok) {
        throw new Error('Failed to generate QR code');
      }

      const { qr_code_url } = await qrResponse.json();
      setQrCodeUrl(qr_code_url);

      // Start polling for payment status
      startPaymentPolling(order.id, subscription_id);
    } catch (err: any) {
      console.error('QR generation error:', err);
      setError(err.message || 'Failed to generate QR code');
      setProcessing(false);
    }
  };

  const startPaymentPolling = (orderId: string, subId: string) => {
    const interval = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          clearInterval(interval);
          setError('Session expired. Please log in again.');
          return;
        }
        
        const statusResponse = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-payment-status`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ order_id: orderId }),
          }
        );

        if (statusResponse.ok) {
          const { status, payment_id } = await statusResponse.json();
          
          if (status === 'paid') {
            setPaymentStatus('success');
            clearInterval(interval);
            onSuccess(subId);
          } else if (status === 'failed') {
            setPaymentStatus('failed');
            setError('Payment failed. Please try again.');
            clearInterval(interval);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds

    setPollingInterval(interval);
  };

  const handleUpiPayment = async () => {
    if (!upiId || !upiId.includes('@')) {
      setError('Please enter a valid UPI ID (e.g., yourname@paytm)');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { order, subscription_id } = await createOrder();
      setSubscriptionId(subscription_id);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Session expired. Please log in again.');
      
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'ShreemAstro',
        description: `${plan.display_name} - 7 Day Free Trial`,
        order_id: order.id,
        method: 'upi',
        prefill: {
          email: userEmail,
          contact: userDetails.cell_number || '',
          vpa: upiId, // UPI ID
        },
        theme: {
          color: '#667eea',
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            setError('Payment cancelled');
          },
        },
        handler: async function (response: any) {
          await verifyPayment(response, subscription_id, session.access_token);
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err: any) {
      setError(err.message);
      setProcessing(false);
    }
  };

  const handleCardPayment = async () => {
    setProcessing(true);
    setError(null);

    try {
      const { order, subscription_id } = await createOrder();
      setSubscriptionId(subscription_id);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Session expired. Please log in again.');

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'ShreemAstro',
        description: `${plan.display_name} - 7 Day Free Trial`,
        order_id: order.id,
        prefill: {
          name: userDetails.full_name || '',
          email: userEmail,
          contact: userDetails.cell_number || '',
        },
        theme: {
          color: '#667eea',
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            setError('Payment cancelled');
          },
        },
        handler: async function (response: any) {
          await verifyPayment(response, subscription_id, session.access_token);
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err: any) {
      setError(err.message);
      setProcessing(false);
    }
  };

  const handleManualPaymentNotify = async () => {
    setProcessing(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Please log in to continue');

      // Send notification to admin about manual payment
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-admin-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            userName: userDetails.full_name || userEmail,
            userEmail: userEmail,
            userPhone: userDetails.cell_number || '',
            selectedPlan: plan.plan_name,
            billingCycle: plan.billing_cycle,
            countryCode: userDetails.country_code || 'IN',
            paymentMethod: 'manual_bank_transfer',
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send notification. Please try again.');
      }

      setManualPaymentNotified(true);
      setProcessing(false);
    } catch (err: any) {
      setError(err.message);
      setProcessing(false);
    }
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const verifyPayment = async (response: any, subId: string, token: string) => {
    try {
      const verifyResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            subscription_id: subId,
          }),
        }
      );

      if (!verifyResponse.ok) {
        throw new Error('Payment verification failed');
      }

      const { success } = await verifyResponse.json();
      if (success) {
        onSuccess(subId);
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (err: any) {
      setError(err.message || 'Payment verification failed');
      setProcessing(false);
    }
  };

  const formatPrice = (price: number) => {
    return currency === 'INR' ? `₹${price.toLocaleString()}` : `$${price.toFixed(2)}`;
  };

  const price = currency === 'INR' ? plan.price_inr : plan.price_usd;
  const { isMobile } = useIsMobile();

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography level="h2" sx={{ mb: 1 }}>
          Complete Your Payment
        </Typography>
        <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
          Secure your 7-day free trial + {plan.display_name}
        </Typography>
      </Box>

      {/* Selected Plan Summary */}
      <Card variant="outlined" sx={{ mb: 3, p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2, mb: { xs: 0, sm: 2 } }}>
          <Box>
            <Typography level="title-lg" sx={{ mb: 0.5 }}>
              {plan.display_name}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {plan.description}
            </Typography>
          </Box>
          <Chip color="primary" variant="soft" size="lg" sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
            {formatPrice(price)}/{plan.billing_cycle === 'monthly' ? 'mo' : 'yr'}
          </Chip>
        </Box>
      </Card>

      {/* Payment Method Tabs */}
      <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val as number)} sx={{ mb: 3 }}>
        <TabList sx={{ justifyContent: 'center', flexWrap: { xs: 'nowrap', sm: 'wrap' } }}>
          <Tab indicatorInset sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}>
            <QrCode2Icon sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>UPI (QR / ID)</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>UPI</Box>
          </Tab>
          <Tab indicatorInset sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}>
            <CreditCardIcon sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Card / NetBanking</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Card</Box>
          </Tab>
          <Tab indicatorInset sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}>
            <AccountBalanceWalletIcon sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Manual Payment</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Manual</Box>
          </Tab>
        </TabList>

        {/* QR Code Payment */}
        <TabPanel value={0} sx={{ px: { xs: 0, sm: 3 } }}>
          <Card variant="outlined" sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Pay via UPI (Recommended)
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
              Fastest & most secure payment method
            </Typography>

            {/* QR Code Section */}
            <Box sx={{ mb: 4 }}>
              <Typography level="title-md" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <QrCode2Icon sx={{ mr: 1 }} />
                Option 1: Scan QR Code
              </Typography>
              <Typography level="body-xs" sx={{ mb: 2, color: 'text.secondary' }}>
                Works with any UPI app (Google Pay, PhonePe, Paytm, BHIM, etc.)
              </Typography>

              {!qrCodeUrl && (
                <Button
                  variant="solid"
                  color="primary"
                  size="lg"
                  onClick={generateQRCode}
                  disabled={processing}
                  startDecorator={processing ? <CircularProgress size="sm" /> : <QrCode2Icon />}
                  sx={{ minWidth: 250 }}
                >
                  {processing ? 'Generating QR Code...' : 'Generate QR Code'}
                </Button>
              )}

              {qrCodeUrl && (
                <Box>
                  <Box
                    sx={{
                      display: 'inline-block',
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 'md',
                      border: '2px solid',
                      borderColor: 'primary.500',
                      mb: 2,
                    }}
                  >
                    <img src={qrCodeUrl} alt="Payment QR Code" style={{ width: 300, height: 300 }} />
                  </Box>

                  {paymentStatus === 'pending' && (
                    <Alert color="warning" variant="soft" sx={{ mt: 2 }}>
                      <Stack spacing={1}>
                        <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                          ⏳ Waiting for payment...
                        </Typography>
                        <Typography level="body-xs">
                          Scan the QR code with your UPI app and complete the payment. This page will automatically update.
                        </Typography>
                        <CircularProgress size="sm" sx={{ mx: 'auto', mt: 2 }} />
                      </Stack>
                    </Alert>
                  )}

                  {paymentStatus === 'success' && (
                    <Alert color="success" variant="soft" sx={{ mt: 2 }}>
                      <CheckCircleIcon sx={{ mr: 1 }} />
                      Payment successful! Redirecting...
                    </Alert>
                  )}

                  <Typography level="body-xs" sx={{ mt: 3, color: 'text.secondary' }}>
                    QR code valid for 15 minutes • Amount: {formatPrice(price)}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }}>OR</Divider>

            {/* UPI ID Section */}
            <Box sx={{ mt: 4 }}>
              <Typography level="title-md" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AccountBalanceWalletIcon sx={{ mr: 1 }} />
                Option 2: Enter UPI ID
              </Typography>
              <Typography level="body-xs" sx={{ mb: 2, color: 'text.secondary' }}>
                Enter your UPI ID to pay directly from your UPI app
              </Typography>

              <FormControl sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
                <FormLabel>UPI ID</FormLabel>
                <Input
                  placeholder="yourname@paytm / yourname@gpay"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  size="lg"
                  disabled={processing}
                  error={upiId.length > 0 && !upiId.includes('@')}
                  sx={{ textAlign: 'center' }}
                />
                {upiId.length > 0 && !upiId.includes('@') && (
                  <Typography level="body-xs" sx={{ color: 'danger.500', mt: 0.5 }}>
                    UPI ID must include @ (e.g., user@paytm)
                  </Typography>
                )}
              </FormControl>

              <Button
                variant="solid"
                color="success"
                size="lg"
                onClick={handleUpiPayment}
                disabled={processing || !upiId || !upiId.includes('@')}
                startDecorator={processing ? <CircularProgress size="sm" /> : <AccountBalanceWalletIcon />}
                sx={{ minWidth: 250 }}
              >
                {processing ? 'Processing...' : `Pay ${formatPrice(price)} via UPI`}
              </Button>

              <Typography level="body-xs" sx={{ mt: 2, color: 'text.secondary' }}>
                You'll be redirected to your UPI app to authorize payment
              </Typography>
            </Box>
          </Card>
        </TabPanel>

        {/* Card Payment */}
        <TabPanel value={1} sx={{ px: { xs: 0, sm: 3 } }}>
          <Card variant="outlined" sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Pay with Card / NetBanking
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
              Secure payment via Credit Card, Debit Card, or NetBanking
            </Typography>

            <Button
              variant="solid"
              color="primary"
              size="lg"
              onClick={handleCardPayment}
              disabled={processing}
              startDecorator={processing ? <CircularProgress size="sm" /> : <CreditCardIcon />}
              sx={{ minWidth: 250 }}
            >
              {processing ? 'Loading...' : `Pay ${formatPrice(price)}`}
            </Button>

            <Typography level="body-xs" sx={{ mt: 2, color: 'text.secondary' }}>
              PCI DSS Compliant • 256-bit SSL Encryption
            </Typography>
          </Card>
        </TabPanel>

        {/* Manual Payment */}
        <TabPanel value={2} sx={{ px: { xs: 0, sm: 3 } }}>
          <Card variant="outlined" sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography level="title-lg" sx={{ mb: 2, textAlign: 'center' }}>
              Manual Payment
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              Pay via UPI or Bank Transfer
            </Typography>

            {!manualPaymentNotified ? (
              <>
                {/* GPay QR Code Section */}
                <Card variant="soft" sx={{ mb: 3, p: 3, bgcolor: 'primary.50', textAlign: 'center' }}>
                  <Typography level="title-md" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <QrCode2Icon sx={{ mr: 1, color: 'primary.500' }} />
                    Quick Payment: Scan QR Code
                  </Typography>
                  <Typography level="body-xs" sx={{ mb: 2, color: 'text.secondary' }}>
                    Scan with any UPI app (GPay, PhonePe, Paytm, BHIM, etc.)
                  </Typography>

                  {/* QR Code Image */}
                  <Box
                    component="img"
                    src="/Assets/gpay-qr-code.png"
                    alt="GPay QR Code"
                    sx={{
                      width: { xs: '100%', sm: 220 },
                      maxWidth: 220,
                      height: 'auto',
                      aspectRatio: '1/1',
                      mx: 'auto',
                      mb: 2,
                      border: '2px solid',
                      borderColor: 'primary.200',
                      borderRadius: 2,
                      p: 1,
                      bgcolor: 'white',
                    }}
                  />

                  <Typography level="body-sm" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Amount: {formatPrice(price)}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'success.600' }}>
                    ✓ Instant payment • UPI ID: <strong>archer111@okaxis</strong>
                  </Typography>
                </Card>

                <Divider sx={{ my: 3 }}>OR</Divider>

                {/* Bank Details */}
                <Card variant="soft" sx={{ mb: 3, p: { xs: 2, sm: 3 }, bgcolor: 'background.level1' }}>
                  <Typography level="title-sm" sx={{ mb: 2, fontWeight: 'bold' }}>
                    📋 Bank Account Details:
                  </Typography>

                  <Stack spacing={2}>
                    {/* Account Name */}
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        Account Name:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography level="body-md" sx={{ fontWeight: 'bold', fontFamily: 'monospace', wordBreak: 'break-word', flex: 1, minWidth: 0 }}>
                          ShreemAstro Services
                        </Typography>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color={copiedField === 'accountName' ? 'success' : 'neutral'}
                          onClick={() => copyToClipboard('ShreemAstro Services', 'accountName')}
                          sx={{ flexShrink: 0 }}
                        >
                          {copiedField === 'accountName' ? <CheckCircleIcon /> : <ContentCopyIcon />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Account Number */}
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        Account Number:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography level="body-md" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                          XXXX XXXX XXXX
                        </Typography>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color={copiedField === 'accountNumber' ? 'success' : 'neutral'}
                          onClick={() => copyToClipboard('XXXXXXXXXXXX', 'accountNumber')}
                        >
                          {copiedField === 'accountNumber' ? <CheckCircleIcon /> : <ContentCopyIcon />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* IFSC Code */}
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        IFSC Code:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography level="body-md" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                          XXXX0XXXXXX
                        </Typography>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color={copiedField === 'ifsc' ? 'success' : 'neutral'}
                          onClick={() => copyToClipboard('XXXX0XXXXXX', 'ifsc')}
                        >
                          {copiedField === 'ifsc' ? <CheckCircleIcon /> : <ContentCopyIcon />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Bank Name */}
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        Bank Name:
                      </Typography>
                      <Typography level="body-md" sx={{ fontWeight: 'bold' }}>
                        HDFC Bank
                      </Typography>
                    </Box>

                    {/* Amount */}
                    <Divider />
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        Amount to Pay:
                      </Typography>
                      <Chip color="danger" variant="soft" size="lg">
                        {formatPrice(price)}
                      </Chip>
                    </Box>
                  </Stack>
                </Card>

                {/* Instructions */}
                <Alert color="warning" variant="soft" sx={{ mb: 3 }}>
                  <Stack spacing={1}>
                    <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                      Payment Instructions:
                    </Typography>
                    <Typography level="body-xs">
                      <strong>Option 1 - UPI (Recommended):</strong>
                    </Typography>
                    <Typography level="body-xs" sx={{ pl: 2 }}>
                      • Scan QR code above with your UPI app
                    </Typography>
                    <Typography level="body-xs" sx={{ pl: 2 }}>
                      • Pay exactly <strong>{formatPrice(price)}</strong>
                    </Typography>
                    <Typography level="body-xs" sx={{ pl: 2, mb: 1 }}>
                      • Click "I Have Made the Payment" below
                    </Typography>
                    <Typography level="body-xs">
                      <strong>Option 2 - Bank Transfer:</strong>
                    </Typography>
                    <Typography level="body-xs" sx={{ pl: 2 }}>
                      • Transfer <strong>{formatPrice(price)}</strong> to account above
                    </Typography>
                    <Typography level="body-xs" sx={{ pl: 2 }}>
                      • Add reference: <strong>"{plan.plan_name.toUpperCase()} - {userEmail}"</strong>
                    </Typography>
                    <Typography level="body-xs" sx={{ pl: 2, mb: 1 }}>
                      • Click button below after transfer
                    </Typography>
                    <Typography level="body-xs" sx={{ fontStyle: 'italic', color: 'warning.600' }}>
                      ⏱️ Your subscription will be activated within 24 hours after verification
                    </Typography>
                  </Stack>
                </Alert>

                {/* Notify Button */}
                <Button
                  variant="solid"
                  color="primary"
                  size="lg"
                  onClick={handleManualPaymentNotify}
                  disabled={processing}
                  fullWidth
                  startDecorator={processing ? <CircularProgress size="sm" /> : <CheckCircleIcon />}
                >
                  {processing ? 'Notifying...' : 'I Have Made the Payment'}
                </Button>

                <Typography level="body-xs" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                  You'll receive a confirmation email once we verify your payment
                </Typography>
              </>
            ) : (
              <Alert color="success" variant="soft">
                <Stack spacing={2} sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon sx={{ fontSize: 48, mx: 'auto' }} />
                  <Typography level="title-md">
                    Thank You! Payment Notification Received
                  </Typography>
                  <Typography level="body-sm">
                    We've received your payment notification for <strong>{plan.display_name}</strong>.
                    Our team will verify your payment within 24 hours.
                  </Typography>
                  <Typography level="body-sm">
                    You'll receive a confirmation email at <strong>{userEmail}</strong> once your
                    subscription is activated.
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Need help? Email us at <strong>care@shreemastro.com</strong>
                  </Typography>
                </Stack>
              </Alert>
            )}
          </Card>
        </TabPanel>
      </Tabs>

      {/* Trial Period Info */}
      <Alert color="success" variant="soft" sx={{ mb: 3 }}>
        <Box>
          <Typography level="title-md" sx={{ mb: 1 }}>
            🎉 7-Day Free Trial Included
          </Typography>
          <Typography level="body-sm">
            Payment of {formatPrice(price)} secures your subscription. Trial starts immediately. Cancel during
            trial for full refund.
          </Typography>
        </Box>
      </Alert>

      {/* Error Display */}
      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Back Button */}
      <Box sx={{ textAlign: 'center' }}>
        <Button variant="outlined" onClick={onBack} disabled={processing}>
          ← Back to Plan Selection
        </Button>
      </Box>

      {/* Trust Badges */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
          Secured by Razorpay • RBI Certified • UPI & Card Payments
        </Typography>
      </Box>
    </Box>
  );
}
